/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Launch a GPU pod - 4-step wizard (console redesign, Oct 2026):
//   1 Choose GPU → 2 Template & image → 3 Storage & access → 4 Review & launch
// with a "Your selection" summary aside. Steps live in components/pods/launch.
//
// Deep links (navigation state): { template?: LaunchTemplate; preselectedGpu?: string }
//   - template: pre-fills the form; GPU must still be picked, then step 2 is skipped
//   - preselectedGpu: auto-selects that GPU type once pricing loads (if in stock)
//   - preselectedVolumeId (+ projectId): pre-selects that NFS volume in step 3
//     ("Attach to new pod" on the NFS volumes page)

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CreateVolumeModal from '../components/pods/CreateVolumeModal';
import CreateSSHKeyModal from '../components/pods/CreateSSHKeyModal';
import {
  containerService,
  templateService,
  pricingService,
  waitlistService,
  sshKeyService,
  volumeService,
} from '../services/api';
import toast from 'react-hot-toast';
import { LaunchTemplate, GPUPricing, BillingPeriod, ResourcePricing, SSHKey, Volume, ContainerCostEstimate, Project } from '../types';
import InsufficientBalanceModal from '../components/wallet/InsufficientBalanceModal';
import { useWallet } from '../hooks/useWallet';
import { useSelectedProject } from '../hooks/useSelectedProject';
import { usePriceUnit } from '../hooks/usePriceUnit';
import { Page, PageHeader, Stepper, KV } from '../components/console/ui';
import Dialog, { Callout } from '../components/pods/Dialog';
import GpuStep from '../components/pods/launch/GpuStep';
import TemplateStep, { CUSTOM_IMAGE } from '../components/pods/launch/TemplateStep';
import ConfigStep from '../components/pods/launch/ConfigStep';
import ReviewStep from '../components/pods/launch/ReviewStep';
import PriceComparisonDialog from '../components/pods/launch/PriceComparisonDialog';
import { LAUNCH_STEPS, LaunchStep, PodForm } from '../components/pods/launch/types';
import {
  BILLING_PERIODS,
  PERIOD_MINUTES,
  isInStock,
  periodShort,
  shortImage,
  useGpuCatalog,
  useSellPrice,
  useTemplateCatalog,
} from '../components/pods/launch/catalog';

const STEP_LABELS: Record<LaunchStep, string> = {
  gpu: 'Choose GPU',
  template: 'Template & image',
  configure: 'Storage & access',
  review: 'Review & launch',
};

const slug = (s: string) =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

const CreatePod: React.FC = () => {
  const navigate = useNavigate();
  const { perMinute } = usePriceUnit();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [step, setStep] = useState<LaunchStep>('gpu');
  const [selectedGPU, setSelectedGPU] = useState<GPUPricing | null>(null);
  // Template arrived via navigation from the Templates page; GPU still has to
  // be selected, then the template step is skipped
  const [templateDeepLinked, setTemplateDeepLinked] = useState(false);
  const [pickedTemplateId, setPickedTemplateId] = useState<string | null>(null);
  const [pickedLabel, setPickedLabel] = useState('');
  const [showPriceComparison, setShowPriceComparison] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDescription, setTemplateDescription] = useState('');
  // Custom templates are project-scoped on the backend; the user picks which
  // project the template is saved to (defaults to the pod's project)
  const [templateProjectId, setTemplateProjectId] = useState('');
  const [showInsufficientBalance, setShowInsufficientBalance] = useState(false);
  const [balanceErrorMessage, setBalanceErrorMessage] = useState('');
  const [notifyingType, setNotifyingType] = useState<string | null>(null);
  const [advisorPrompt, setAdvisorPrompt] = useState('');
  const preselectGpuRef = useRef<string | null>(null);
  const preselectVolumeRef = useRef<string | null>(null);

  // Wallet data for balance check / coverage
  const { availableBalance, minimumRequired } = useWallet();

  // Volume mounting state
  const [showCreateVolumeModal, setShowCreateVolumeModal] = useState(false);
  const [selectedVolume, setSelectedVolume] = useState<Volume | null>(null);
  const [volumeMountPath, setVolumeMountPath] = useState('/data');

  // SSH key modal state
  const [showCreateSSHKeyModal, setShowCreateSSHKeyModal] = useState(false);

  const [formData, setFormData] = useState<PodForm>({
    name: '',
    project_id: '',
    image: 'pytorch/pytorch:2.0.1-cuda11.7-cudnn8-runtime',
    cpu_cores: 4,
    memory_gb: 16,
    storage_gb: 10,
    gpu_count: 1,
    gpu_cores: 100,
    gpu_type: '',
    replicas: 1,
    billing_period: 'minute' as BillingPeriod,
    env_vars: {},
    ports: [8888],
    is_private_registry: false,
    registry_url: '',
    registry_username: '',
    registry_password: '',
    entry_point_command: '',
    ssh_key_id: '',
  });

  // Track if entry point command came from a template
  const [isTemplateCommand, setIsTemplateCommand] = useState(false);

  // Use the global selected project hook
  const { selectedProjectId, projects, setSelectedProject, autoSelectFirstProject } = useSelectedProject();

  const { gpus: allGpus, isLoading: loadingGPU, getAvailability: getGPUAvailability } = useGpuCatalog();
  const { gstRate, markupPct, gatewayPct, foldSellPrice } = useSellPrice();

  const { data: resourcePricingData } = useQuery({
    queryKey: ['resource-pricing'],
    queryFn: () => pricingService.getResourcePricing().then((res) => res.data),
  });

  // Helper to get resource pricing by type
  const getResourcePricing = (resourceType: string): ResourcePricing | undefined => {
    const pricingList = Array.isArray(resourcePricingData) ? resourcePricingData : resourcePricingData?.data || [];
    return pricingList.find((p: ResourcePricing) => p.resource_type === resourceType);
  };

  // "Notify me" on sold-out GPUs → waitlist
  const handleJoinWaitlist = async (gpuType: string) => {
    setNotifyingType(gpuType);
    try {
      const response = await waitlistService.join({
        gpu_type: gpuType,
        gpu_count: 1,
        project_id: formData.project_id || (projects.length > 0 ? projects[0].id : undefined),
      });
      const queuePosition = response.data?.queue_position || 1;
      const totalInQueue = response.data?.total_in_queue || 1;
      toast.success(`Added to waitlist for ${gpuType}! Queue position: #${queuePosition} of ${totalInQueue}. Check your email for confirmation.`);
    } catch (error) {
      // Error is handled by the API interceptor
    } finally {
      setNotifyingType(null);
    }
  };

  const { templates: catalogTemplates, isLoading: loadingCatalog } = useTemplateCatalog();

  // User's own saved templates, shown alongside the global catalog
  const { data: myTemplatesData } = useQuery({
    queryKey: ['my-templates'],
    queryFn: () => templateService.getMyTemplates().then((res) => res.data),
  });
  const myTemplates: LaunchTemplate[] = useMemo(
    () =>
      (Array.isArray(myTemplatesData) ? myTemplatesData : myTemplatesData?.data || []).filter(
        (t: LaunchTemplate) => t.type === 'pod' && t.scope === 'custom',
      ),
    [myTemplatesData],
  );
  const myTemplateIds = useMemo(() => new Set(myTemplates.map((t) => t.id)), [myTemplates]);
  const catalogOnly = useMemo(() => catalogTemplates.filter((t) => !myTemplateIds.has(t.id)), [catalogTemplates, myTemplateIds]);

  const lateStep = step === 'configure' || step === 'review';

  // Fetch SSH keys
  const { data: sshKeysData, refetch: refetchSSHKeys } = useQuery({
    queryKey: ['ssh-keys'],
    queryFn: () => sshKeyService.list().then((res) => res.data),
    enabled: lateStep,
  });
  const sshKeys: SSHKey[] = Array.isArray(sshKeysData) ? sshKeysData : sshKeysData?.data || sshKeysData?.keys || [];

  // Fetch volumes for the selected project
  const { data: volumesData, refetch: refetchVolumes } = useQuery({
    queryKey: ['volumes', formData.project_id],
    queryFn: () => volumeService.list(formData.project_id).then((res) => res.data),
    enabled: lateStep && !!formData.project_id,
  });
  // Only active volumes can be mounted
  const availableVolumes: Volume[] = useMemo(() => {
    const volumes: Volume[] = Array.isArray(volumesData) ? volumesData : volumesData?.data || volumesData?.storages || [];
    return volumes.filter((v) => v.status === 'active');
  }, [volumesData]);

  // Pre-select a deep-linked NFS volume once the project's volumes load
  useEffect(() => {
    const want = preselectVolumeRef.current;
    if (!want || availableVolumes.length === 0) return;
    const vol = availableVolumes.find((v) => v.id === want);
    if (vol) {
      preselectVolumeRef.current = null;
      setSelectedVolume(vol);
    }
  }, [availableVolumes]);

  // Handle template / GPU / volume passed via navigation state
  useEffect(() => {
    const state = location.state as {
      template?: LaunchTemplate;
      preselectedGpu?: string;
      preselectedVolumeId?: string;
      projectId?: string;
    } | null;
    if (state?.preselectedGpu) preselectGpuRef.current = state.preselectedGpu;
    if (state?.preselectedVolumeId) {
      preselectVolumeRef.current = state.preselectedVolumeId;
      // Volumes are project-scoped: switch to the volume's project first
      if (state.projectId) {
        setFormData((prev) => ({ ...prev, project_id: state.projectId! }));
        setSelectedProject(state.projectId);
      }
    }
    if (state?.template) {
      const template = state.template;
      setFormData((prev) => ({
        ...prev,
        name: `${template.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now().toString(36)}`,
        image: template.image_name,
        cpu_cores: template.cpu_cores || prev.cpu_cores,
        memory_gb: template.memory_gb || prev.memory_gb,
        storage_gb: template.storage_gb || prev.storage_gb,
        env_vars: template.env_variables || {},
        ports: template.ports || [8888],
        entry_point_command: template.init_command || '',
      }));
      if (template.init_command) setIsTemplateCommand(true);
      setPickedTemplateId(template.id);
      setPickedLabel(template.name);
      // Only GPU instances can be provisioned, so a deep-linked template must
      // still go through GPU selection (gpu_memory_gb is computed from the
      // selected GPU's VRAM). After the GPU is picked, the template step is
      // skipped since the template is already applied.
      setTemplateDeepLinked(true);
      setStep('gpu');
    }
    if (state?.template || state?.preselectedGpu || state?.preselectedVolumeId) {
      // Clear the navigation state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Auto-select first project if none selected globally
  useEffect(() => {
    autoSelectFirstProject();
  }, [autoSelectFirstProject]);

  // Sync formData.project_id with global selectedProjectId
  useEffect(() => {
    if (selectedProjectId && formData.project_id !== selectedProjectId) {
      setFormData((prev) => ({ ...prev, project_id: selectedProjectId }));
    }
  }, [selectedProjectId, formData.project_id]);

  const createMutation = useMutation({
    mutationFn: () => {
      // Port names must be lowercase alphanumeric with hyphens (DNS-compatible)
      const portsForBackend = formData.ports.map((port) => ({
        container_port: port,
        protocol: 'TCP',
        name: `tcp-${port}`,
      }));

      // gpu_count = number of physical GPUs; gpu_cores = percentage per GPU.
      // GPU memory = VRAM × count × percentage, e.g. 2 GPUs at 50% of 48GB = 48GB
      const gpuMemoryGb =
        selectedGPU && formData.gpu_count > 0
          ? Math.round((selectedGPU.memory_gb || 0) * formData.gpu_count * (formData.gpu_cores / 100) * 100) / 100
          : 0;

      const payload: Parameters<typeof containerService.create>[0] = {
        name: formData.name,
        project_id: formData.project_id,
        image: formData.image,
        cpu_cores: formData.cpu_cores,
        memory_gb: formData.memory_gb,
        storage_gb: formData.storage_gb,
        // GPU fields are only sent when a GPU was actually selected - a CPU-only
        // pod must not carry gpu_cores/gpu_type, or backend validation rejects it
        gpu_count: selectedGPU && formData.gpu_count > 0 ? formData.gpu_count : undefined,
        gpu_cores: selectedGPU && formData.gpu_count > 0 ? formData.gpu_cores : undefined,
        gpu_memory_gb: gpuMemoryGb > 0 ? gpuMemoryGb : undefined,
        gpu_type: selectedGPU ? formData.gpu_type || undefined : undefined,
        replicas: formData.replicas,
        billing_period: formData.billing_period,
        ports: portsForBackend.length > 0 ? portsForBackend : undefined,
        ssh_key_id: formData.ssh_key_id || undefined,
      };

      // Add env_vars only if not empty (drop empty keys)
      const filteredEnvVars: Record<string, string> = {};
      Object.entries(formData.env_vars).forEach(([key, value]) => {
        if (key.trim()) filteredEnvVars[key.trim()] = value;
      });
      if (Object.keys(filteredEnvVars).length > 0) payload.env_vars = filteredEnvVars;

      if (formData.entry_point_command.trim()) payload.command = formData.entry_point_command.trim();

      if (formData.is_private_registry) {
        payload.registry_url = formData.registry_url || undefined;
        payload.registry_username = formData.registry_username || undefined;
        payload.registry_password = formData.registry_password || undefined;
      }

      // NFS volume mount
      if (selectedVolume && volumeMountPath) {
        const nfsServer = selectedVolume.nfs_server_ip || selectedVolume.nfs_server || '';
        const nfsPath = selectedVolume.nfs_export_path || selectedVolume.nfs_path || '';
        if (nfsServer && nfsPath) {
          (payload as any).nfs_mounts = [
            {
              volume_id: selectedVolume.id,
              name: selectedVolume.name,
              server: nfsServer,
              path: nfsPath,
              mount_path: volumeMountPath,
            },
          ];
        }
      }

      return containerService.create(payload);
    },
    onSuccess: () => {
      toast.success('Pod is being created');
      navigate('/pods');
    },
    onError: (error: any) => {
      // Insufficient balance (HTTP 402)
      if (error.response?.status === 402 || error.response?.data?.error === 'insufficient_balance') {
        setBalanceErrorMessage(error.response?.data?.message || 'Insufficient wallet balance to create this resource.');
        setShowInsufficientBalance(true);
      } else {
        const responseData = error.response?.data;
        if (responseData?.errors && Array.isArray(responseData.errors)) {
          const errorMessages = responseData.errors
            .map((e: { field: string; message: string }) => `${e.field}: ${e.message}`)
            .join(', ');
          toast.error(`Validation failed: ${errorMessages}`);
          console.error('Validation errors:', responseData.errors);
        } else {
          toast.error(responseData?.error || responseData?.message || 'Failed to create pod');
        }
      }
    },
  });

  const saveTemplateMutation = useMutation({
    mutationFn: () =>
      templateService.create({
        name: templateName,
        description: templateDescription,
        type: 'pod',
        scope: 'custom',
        // Custom templates are project-scoped; the backend requires project_id
        // and a matching X-Project-Id header (set by templateService.create)
        project_id: templateProjectId,
        image_name: formData.image,
        cpu_cores: formData.cpu_cores,
        memory_gb: formData.memory_gb,
        // Only send GPU fields when a GPU is selected. gpu_cores is stored as
        // fraction (0-1) for template compatibility
        ...(formData.gpu_type ? { gpu_cores: formData.gpu_cores / 100, gpu_type: formData.gpu_type } : {}),
        env_variables: formData.env_vars,
        ports: formData.ports,
        init_command: formData.entry_point_command,
      }),
    onSuccess: () => {
      toast.success('Template saved successfully');
      setShowSaveTemplate(false);
      setTemplateName('');
      setTemplateDescription('');
      queryClient.invalidateQueries({ queryKey: ['my-templates'] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to save template');
    },
  });

  const handleSelectGPU = useCallback((gpu: GPUPricing) => {
    setSelectedGPU(gpu);
    // Auto-populate with GPU's default/included resources
    setFormData((prev) => ({
      ...prev,
      gpu_type: gpu.gpu_type,
      gpu_count: 1, // Start with 1 physical GPU
      gpu_cores: 100, // Start with 100% of each GPU
      cpu_cores: gpu.included_cpu_cores || 4,
      memory_gb: gpu.included_memory_gb || 16,
    }));
  }, []);

  // Deep-linked GPU (e.g. "Launch L40S" from the Pods empty state)
  useEffect(() => {
    const want = preselectGpuRef.current;
    if (!want || allGpus.length === 0) return;
    const gpu = allGpus.find((g) => g.gpu_type === want);
    if (!gpu) {
      preselectGpuRef.current = null;
      return;
    }
    // Wait for availability; selecting a sold-out GPU isn't allowed
    if (isInStock(getGPUAvailability(gpu.gpu_type))) {
      preselectGpuRef.current = null;
      handleSelectGPU(gpu);
    }
  }, [allGpus, getGPUAvailability, handleSelectGPU]);

  const handleSelectTemplate = (template: LaunchTemplate | null) => {
    if (template) {
      setFormData((prev) => ({
        ...prev,
        name: `${template.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now().toString(36)}`,
        image: template.image_name,
        cpu_cores: template.cpu_cores || prev.cpu_cores,
        memory_gb: template.memory_gb || prev.memory_gb,
        storage_gb: template.storage_gb || prev.storage_gb,
        env_vars: template.env_variables || {},
        ports: template.ports || [8888],
        // Template gpu_cores field is stored as fraction (0-1), convert to percentage.
        // Never resurrect GPU settings when the user chose a CPU-only instance.
        gpu_count: selectedGPU ? prev.gpu_count || 1 : 0,
        gpu_cores: selectedGPU && template.gpu_cores ? Math.round(template.gpu_cores * 100) : prev.gpu_cores,
        gpu_type: selectedGPU ? template.gpu_type || prev.gpu_type : '',
        entry_point_command: template.init_command || '',
      }));
      setIsTemplateCommand(!!template.init_command);
      setPickedTemplateId(template.id);
      setPickedLabel(template.name);
    } else {
      // Own image: keep whatever image is in the form
      setIsTemplateCommand(false);
      setPickedTemplateId(CUSTOM_IMAGE);
      setPickedLabel('');
    }
  };

  // Included CPU/RAM scale with GPU count and percentage
  const getIncludedCPU = (): number => {
    if (!selectedGPU || formData.gpu_count === 0) return 0;
    return Math.ceil((selectedGPU.included_cpu_cores || 4) * formData.gpu_count * (formData.gpu_cores / 100));
  };
  const getIncludedMemory = (): number => {
    if (!selectedGPU || formData.gpu_count === 0) return 0;
    return Math.ceil((selectedGPU.included_memory_gb || 16) * formData.gpu_count * (formData.gpu_cores / 100));
  };
  const getEffectiveGPUVRAM = (): number => {
    if (!selectedGPU || formData.gpu_count === 0) return 0;
    return (selectedGPU.memory_gb || 0) * formData.gpu_count * (formData.gpu_cores / 100);
  };

  // Live launch-time capacity for the selected GPU spec. `ceiling` clamps the
  // slider max so users can't request what no cluster can host; `free` drives
  // the "X free now" caption. Refetched on spec change and every 15s.
  const { data: launchCapacity } = useQuery({
    queryKey: ['launch-capacity', selectedProjectId, formData.gpu_type, formData.gpu_count, formData.gpu_cores],
    queryFn: () =>
      containerService
        .getLaunchCapacity(selectedProjectId!, {
          gpu_type: formData.gpu_type,
          gpu_count: formData.gpu_count,
          gpu_cores_pct: formData.gpu_cores,
        })
        .then((res) => res.data as import('../services/api').LaunchCapacityResponse),
    enabled: lateStep && !!selectedProjectId,
    refetchInterval: 15000,
    staleTime: 10000,
  });

  // Slider hard max - falls back to the previous hard-coded values until the
  // first capacity payload lands. ceiling=0 (no cluster carries this spec)
  // collapses the slider to 1 so the user sees it can't be hosted.
  const cpuMax = launchCapacity ? Math.max(1, Math.floor(launchCapacity.ceiling.cpu_cores)) : 128;
  const memoryMax = launchCapacity ? Math.max(1, Math.floor(launchCapacity.ceiling.memory_gb)) : 512;
  const cpuFree = launchCapacity ? Math.floor(launchCapacity.free.cpu_cores) : null;
  const memoryFree = launchCapacity ? Math.floor(launchCapacity.free.memory_gb) : null;
  const noCluster = !!launchCapacity && launchCapacity.matched_clusters === 0;

  // Pull values above a new ceiling down so slider and payload stay in sync.
  useEffect(() => {
    if (!launchCapacity) return;
    setFormData((prev) => {
      const nextCPU = Math.min(prev.cpu_cores, cpuMax);
      const nextMem = Math.min(prev.memory_gb, memoryMax);
      if (nextCPU === prev.cpu_cores && nextMem === prev.memory_gb) return prev;
      return { ...prev, cpu_cores: nextCPU, memory_gb: nextMem };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cpuMax, memoryMax]);

  const getPeriodHours = (period: BillingPeriod): number => (PERIOD_MINUTES[period] || 1) / 60;

  const getDiscount = (
    period: BillingPeriod,
    pricing: { hourly_discount?: number; daily_discount?: number; monthly_discount?: number; quarter_discount?: number; annual_discount?: number },
  ): number => {
    switch (period) {
      case 'hour': return pricing.hourly_discount || 0;
      case 'day': return pricing.daily_discount || 0;
      case 'month': return pricing.monthly_discount || 0;
      case 'quarter': return pricing.quarter_discount || 0;
      case 'annual': return pricing.annual_discount || 0;
      default: return 0; // No discount for per-minute billing
    }
  };

  // Client-side fallback estimate (base cost for the billing period)
  const calculateEstimatedCost = () => {
    const period = formData.billing_period;
    const periodMinutes = PERIOD_MINUTES[period] || 1;
    let gpuCost = 0;
    let cpuCost = 0;
    let memoryCost = 0;

    if (selectedGPU && formData.gpu_count > 0) {
      const gpuMinuteRate = selectedGPU.price_per_minute * (1 - getDiscount(period, selectedGPU) / 100);
      gpuCost = gpuMinuteRate * formData.gpu_count * (formData.gpu_cores / 100) * periodMinutes;

      const extraCPU = Math.max(0, formData.cpu_cores - getIncludedCPU());
      const cpuPricing = getResourcePricing('cpu');
      if (extraCPU > 0 && cpuPricing) {
        cpuCost = cpuPricing.price_per_unit_per_minute * (1 - getDiscount(period, cpuPricing) / 100) * extraCPU * periodMinutes;
      }
      const extraMemory = Math.max(0, formData.memory_gb - getIncludedMemory());
      const memoryPricing = getResourcePricing('memory');
      if (extraMemory > 0 && memoryPricing) {
        memoryCost =
          memoryPricing.price_per_unit_per_minute * (1 - getDiscount(period, memoryPricing) / 100) * extraMemory * periodMinutes;
      }
    } else {
      // CPU-only instance - charge for all CPU and memory
      const cpuPricing = getResourcePricing('cpu');
      const memoryPricing = getResourcePricing('memory');
      if (cpuPricing) {
        cpuCost = cpuPricing.price_per_unit_per_minute * (1 - getDiscount(period, cpuPricing) / 100) * formData.cpu_cores * periodMinutes;
      }
      if (memoryPricing) {
        memoryCost =
          memoryPricing.price_per_unit_per_minute * (1 - getDiscount(period, memoryPricing) / 100) * formData.memory_gb * periodMinutes;
      }
    }
    return { gpu: gpuCost, cpu: cpuCost, memory: memoryCost, total: gpuCost + cpuCost + memoryCost };
  };

  // Live cost estimate from the backend. This is the authoritative source -
  // calculateEstimatedCost() is only a fallback for offline / API-error cases,
  // so the displayed estimate can't silently diverge from the wallet debit.
  const gpuFraction = selectedGPU && formData.gpu_count > 0 ? formData.gpu_count * (formData.gpu_cores / 100) : 0;
  const { data: backendEstimate, isFetching: isEstimating } = useQuery<ContainerCostEstimate>({
    queryKey: [
      'container-estimate',
      formData.project_id,
      formData.cpu_cores,
      formData.memory_gb,
      formData.storage_gb,
      gpuFraction,
      formData.gpu_type,
      formData.billing_period,
    ],
    queryFn: () =>
      containerService
        .estimateCost(formData.project_id, {
          cpu_cores: formData.cpu_cores,
          memory_gb: formData.memory_gb,
          storage_gb: formData.storage_gb,
          gpu_fraction: gpuFraction,
          gpu_type: formData.gpu_type || undefined,
          billing_period: formData.billing_period,
        })
        .then((res) => res.data as ContainerCostEstimate),
    enabled: step !== 'gpu' && !!selectedGPU && !!formData.project_id && formData.cpu_cores > 0 && formData.memory_gb > 0,
    staleTime: 15_000,
  });

  // Customer sees Price (sales price incl. markup + gateway) → GST → Total.
  const costBreakdown = (() => {
    const cost = backendEstimate?.period_cost ?? calculateEstimatedCost().total;
    const gstPct = backendEstimate?.gst_rate_percent ?? gstRate;
    const salesPrice = backendEstimate?.sales_price ?? cost * (1 + markupPct / 100) * (1 + gatewayPct / 100);
    const gstAmount = backendEstimate?.gst_amount ?? (salesPrice * gstPct) / 100;
    const total = backendEstimate?.period_cost_with_gst ?? salesPrice + gstAmount;
    return { salesPrice, gstPct, gstAmount, total };
  })();

  // ₹/hr shown in the aside. Step 1 is GPU-only (no backend estimate yet);
  // later steps use the full estimate for the chosen billing period.
  const hourly: number | null = !selectedGPU
    ? null
    : step === 'gpu'
      ? foldSellPrice(selectedGPU.price_per_minute) * 60 * formData.gpu_count * (formData.gpu_cores / 100)
      : costBreakdown.total / getPeriodHours(formData.billing_period);
  const walletHours = hourly && hourly > 0 ? Math.floor(availableBalance / hourly) : null;

  const projectName = (projects as Project[]).find((p) => p.id === formData.project_id)?.name;
  const imageLabel = pickedLabel || shortImage(formData.image);
  const selectedSSHKey = sshKeys.find((k) => k.id === formData.ssh_key_id);
  const periodLabel = BILLING_PERIODS.find((b) => b.value === formData.billing_period)?.label || formData.billing_period;

  const stepIndex = LAUNCH_STEPS.indexOf(step);
  const stepLabels = LAUNCH_STEPS.map((s, i) => {
    if (i >= stepIndex) return STEP_LABELS[s];
    if (s === 'gpu' && selectedGPU) return `${selectedGPU.gpu_type} selected`;
    if (s === 'template' && (pickedLabel || pickedTemplateId)) return pickedLabel || 'Custom image';
    return STEP_LABELS[s];
  });

  const goTo = (next: LaunchStep) => {
    setStep(next);
    // New step content starts at the top (the aside sits below on mobile)
    if (!/jsdom/i.test(navigator.userAgent)) window.scrollTo({ top: 0 });
  };

  const continueFromGpu = () => goTo(templateDeepLinked ? 'configure' : 'template');
  const continueFromTemplate = () => {
    if (!formData.name.trim()) {
      const base = slug(`${pickedLabel || shortImage(formData.image)}-${formData.gpu_type}`) || 'pod';
      setFormData((prev) => ({ ...prev, name: `${base}-${Date.now().toString(36)}` }));
    }
    goTo('configure');
  };
  const back = () => {
    if (step === 'template') goTo('gpu');
    else if (step === 'configure') goTo(templateDeepLinked ? 'gpu' : 'template');
    else if (step === 'review') goTo('configure');
  };

  const configReady = !!formData.name && !!formData.project_id;

  const openSaveTemplate = () => {
    setTemplateProjectId(formData.project_id || selectedProjectId || '');
    setShowSaveTemplate(true);
  };

  const askAdvisor = (e: React.FormEvent) => {
    e.preventDefault();
    const q = advisorPrompt.trim();
    navigate(q ? `/advisor?q=${encodeURIComponent(q)}` : '/advisor', { state: q ? { prompt: q } : undefined });
  };

  return (
    <Page wide>
      <PageHeader
        back={{ to: '/pods', label: 'GPU Pods' }}
        title={step === 'review' ? 'Review and launch' : 'Launch a pod'}
      />

      <Stepper steps={stepLabels} current={stepIndex} onStep={(i) => goTo(LAUNCH_STEPS[i])} />

      <div className="flex flex-wrap gap-[22px] items-start">
        <div className="flex-[3_1_640px] min-w-0 flex flex-col gap-4">
          {step === 'gpu' && (
            <GpuStep
              gpus={allGpus}
              loading={loadingGPU}
              getAvailability={getGPUAvailability}
              foldSellPrice={foldSellPrice}
              selectedType={selectedGPU?.gpu_type}
              onSelect={handleSelectGPU}
              onNotify={handleJoinWaitlist}
              notifyingType={notifyingType}
            />
          )}

          {step === 'template' && (
            <TemplateStep
              myTemplates={myTemplates}
              catalog={catalogOnly}
              loading={loadingCatalog}
              pickedId={pickedTemplateId}
              onPick={handleSelectTemplate}
              form={formData}
              onFormChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
            />
          )}

          {step === 'configure' && (
            <ConfigStep
              form={formData}
              setForm={setFormData}
              projects={projects as Project[]}
              onProjectChange={(id) => {
                setFormData((prev) => ({ ...prev, project_id: id }));
                // Also update the global project selection
                if (id) setSelectedProject(id);
              }}
              selectedGPU={selectedGPU}
              gpuAvailability={getGPUAvailability(formData.gpu_type)}
              includedCpu={getIncludedCPU()}
              includedMemory={getIncludedMemory()}
              effectiveVram={getEffectiveGPUVRAM()}
              cpuMax={cpuMax}
              memoryMax={memoryMax}
              cpuFree={cpuFree}
              memoryFree={memoryFree}
              noCluster={noCluster}
              volumes={availableVolumes}
              selectedVolume={selectedVolume}
              onSelectVolume={setSelectedVolume}
              mountPath={volumeMountPath}
              onMountPathChange={setVolumeMountPath}
              onCreateVolume={() => setShowCreateVolumeModal(true)}
              sshKeys={sshKeys}
              onCreateSSHKey={() => setShowCreateSSHKeyModal(true)}
              isTemplateCommand={isTemplateCommand}
              setIsTemplateCommand={setIsTemplateCommand}
              onCompareSavings={() => setShowPriceComparison(true)}
            />
          )}

          {step === 'review' && (
            <ReviewStep
              form={formData}
              projectName={projectName}
              selectedGPU={selectedGPU}
              includedCpu={getIncludedCPU()}
              includedMemory={getIncludedMemory()}
              imageLabel={imageLabel}
              volume={selectedVolume}
              mountPath={volumeMountPath}
              sshKey={selectedSSHKey}
              onEdit={goTo}
              onSaveTemplate={openSaveTemplate}
            />
          )}
        </div>

        <aside
          className="ps-card flex-[1_1_300px] p-5 flex flex-col gap-3.5 lg:sticky lg:top-20"
          aria-label={step === 'review' ? 'Cost' : 'Selection summary'}
        >
          {step !== 'review' ? (
            <>
              <h2 className="m-0 text-base font-medium">Your selection</h2>
              <KV
                k="GPU"
                v={
                  selectedGPU
                    ? `${formData.gpu_count} × ${selectedGPU.gpu_type} ${selectedGPU.memory_gb} GB${formData.gpu_cores < 100 ? ` @ ${formData.gpu_cores}%` : ''}`
                    : 'Not selected'
                }
              />
              <KV k="Project" v={projectName || '-'} />
              {step !== 'gpu' && <KV k="Image" v={imageLabel || '-'} />}
              {step === 'configure' && (
                <>
                  <KV k="vCPU / RAM" v={`${formData.cpu_cores} · ${formData.memory_gb} GB`} />
                  <KV k="Volume" v={selectedVolume?.name || 'None'} />
                </>
              )}
              <KV k="Billing" v={periodLabel} />
              <div className="border-t border-ps-border pt-3.5 flex justify-between items-baseline">
                <span className="text-sm text-ps-muted">Estimated</span>
                {perMinute ? (
                  <span className="text-right">
                    <span className="text-2xl font-semibold">{hourly != null ? `₹${(hourly / 60).toFixed(2)}` : '-'}</span>
                    <span className="text-ps-muted">/min</span>
                    {hourly != null && (
                      <span className="block text-xs text-ps-subtle">₹{hourly >= 100 ? hourly.toFixed(0) : hourly.toFixed(2)}/hr</span>
                    )}
                  </span>
                ) : (
                  <span>
                    <span className="text-2xl font-semibold">{hourly != null ? `₹${hourly >= 100 ? hourly.toFixed(0) : hourly.toFixed(2)}` : '-'}</span>
                    <span className="text-ps-muted">/hr</span>
                  </span>
                )}
              </div>
              <div className="text-xs text-ps-subtle leading-relaxed">
                {step === 'gpu' ? 'GPU only, incl. taxes & fees.' : backendEstimate ? 'Live rate from pricing API, incl. taxes.' : isEstimating ? 'Fetching live rate…' : 'Client-side estimate, incl. taxes.'}
                {walletHours != null && ` Wallet covers about ${walletHours.toLocaleString('en-IN')} hours.`}
                {step === 'configure' && selectedVolume && ' Volumes are billed separately.'}
              </div>

              {step === 'gpu' ? (
                <button type="button" className="ps-btn w-full" onClick={continueFromGpu} disabled={!selectedGPU}>
                  {templateDeepLinked ? 'Continue' : 'Continue to template'}
                </button>
              ) : (
                <div className="flex gap-2.5">
                  <button type="button" className="ps-btn2" onClick={back}>
                    Back
                  </button>
                  {step === 'template' ? (
                    <button type="button" className="ps-btn flex-1" onClick={continueFromTemplate} disabled={!formData.image.trim()}>
                      Continue
                    </button>
                  ) : (
                    <button type="button" className="ps-btn flex-1" onClick={() => goTo('review')} disabled={!configReady}>
                      Review
                    </button>
                  )}
                </div>
              )}
              {step === 'gpu' && !selectedGPU && !loadingGPU && (
                <p className="m-0 text-xs text-ps-subtle">Select an in-stock GPU to continue.</p>
              )}
              {step === 'configure' && !configReady && (
                <p className="m-0 text-xs text-ps-subtle">Pod name and project are required.</p>
              )}
              {step === 'configure' && (
                <button type="button" onClick={openSaveTemplate} className="ps-link text-sm text-left min-h-[32px]">
                  Save as template
                </button>
              )}

              {step === 'gpu' && (
                <form onSubmit={askAdvisor} className="border-t border-ps-border pt-3.5 flex flex-col gap-2">
                  <div className="ps-eyebrow">Compute Advisor</div>
                  <label htmlFor="advisor-ask" className="text-sm text-ps-muted">
                    Not sure? Describe the workload.
                  </label>
                  <input
                    id="advisor-ask"
                    className="ps-input"
                    placeholder="Serve Llama 3 8B for a chatbot"
                    value={advisorPrompt}
                    onChange={(e) => setAdvisorPrompt(e.target.value)}
                  />
                  <button type="submit" className="ps-btn2 w-full">
                    Ask the advisor
                  </button>
                </form>
              )}
            </>
          ) : (
            <>
              <h2 className="m-0 text-base font-medium">Cost</h2>
              {hourly != null && (
                <>
                  <KV k="Per minute" v={`₹${(hourly / 60).toFixed(2)}`} />
                  <KV k="Per hour" v={`₹${hourly.toFixed(2)}`} />
                  <KV k="Per day if left on" v={`₹${(hourly * 24).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`} />
                </>
              )}
              <div className="rounded-md border border-ps-border bg-ps-chrome p-3 flex flex-col gap-1 text-xs">
                {backendEstimate && backendEstimate.discount_percent > 0 && (
                  <div className="flex justify-between text-ps-ok">
                    <span>Period discount</span>
                    <span>−{backendEstimate.discount_percent.toFixed(0)}%</span>
                  </div>
                )}
                <div className="flex justify-between text-ps-muted">
                  <span>Price</span>
                  <span className="font-mono">₹{costBreakdown.salesPrice.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-ps-subtle">
                  <span>GST ({costBreakdown.gstPct.toFixed(0)}%)</span>
                  <span className="font-mono">+ ₹{costBreakdown.gstAmount.toFixed(2)}</span>
                </div>
                <div className="flex justify-between border-t border-ps-border pt-1 text-sm">
                  <span className="font-medium">Total</span>
                  <span className="font-mono font-semibold text-ps-accent">
                    ₹{costBreakdown.total.toFixed(2)}/{periodShort(formData.billing_period)}
                  </span>
                </div>
              </div>
              <div className="text-[11px] text-ps-subtle text-right">
                {backendEstimate ? 'Live rate from pricing API' : isEstimating ? 'Fetching live rate…' : 'Using client-side estimate'}
              </div>
              <div className="border-t border-ps-border pt-3.5 text-[13px] text-ps-muted leading-relaxed">
                Billed {periodLabel.toLowerCase()} from your wallet ({`₹${availableBalance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}) while
                running. Prices include taxes.
                {walletHours != null && ` That covers about ${walletHours.toLocaleString('en-IN')} hours.`}
              </div>
              {noCluster && (
                <Callout tone="err">No cluster can currently host this GPU spec. Adjust it in Storage &amp; access.</Callout>
              )}
              <div className="flex gap-2.5">
                <button type="button" className="ps-btn2" onClick={back}>
                  Back
                </button>
                <button
                  type="button"
                  className="ps-btn flex-1"
                  onClick={() => createMutation.mutate()}
                  disabled={!configReady || createMutation.isPending}
                >
                  {createMutation.isPending ? 'Launching…' : 'Launch pod'}
                </button>
              </div>
            </>
          )}
        </aside>
      </div>

      <PriceComparisonDialog
        open={showPriceComparison}
        onClose={() => setShowPriceComparison(false)}
        form={formData}
        selectedGPU={selectedGPU}
        getResourcePricing={getResourcePricing}
        includedCpu={getIncludedCPU()}
        includedMemory={getIncludedMemory()}
      />

      {/* Save Template Modal */}
      <Dialog
        open={showSaveTemplate}
        onClose={() => setShowSaveTemplate(false)}
        title="Save as template"
        size="md"
        footer={
          <>
            <button type="button" className="ps-btn2" onClick={() => setShowSaveTemplate(false)}>
              Cancel
            </button>
            <button
              type="button"
              className="ps-btn"
              onClick={() => saveTemplateMutation.mutate()}
              disabled={!templateName || !templateProjectId || saveTemplateMutation.isPending}
            >
              {saveTemplateMutation.isPending ? 'Saving...' : 'Save Template'}
            </button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <label className="block">
            <span className="ps-label">Template name</span>
            <input
              type="text"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              className="ps-input"
              placeholder="My PyTorch Template"
            />
          </label>
          <label className="block">
            <span className="ps-label">
              Description <span className="text-ps-subtle">(optional)</span>
            </span>
            <textarea
              value={templateDescription}
              onChange={(e) => setTemplateDescription(e.target.value)}
              className="ps-input py-2"
              rows={3}
              placeholder="Describe what this template is for..."
            />
          </label>
          <div>
            <span className="ps-label">Save to project</span>
            <select
              aria-label="Save to Project"
              value={templateProjectId}
              onChange={(e) => setTemplateProjectId(e.target.value)}
              className="ps-input"
            >
              <option value="">Select a project</option>
              {(projects as Project[]).map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name}
                </option>
              ))}
            </select>
            <p className="m-0 mt-1 text-xs text-ps-subtle">The template will be available to members of this project.</p>
          </div>
          <Callout tone="info">
            <span className="font-semibold">Current configuration:</span> {formData.gpu_type || 'No GPU'}, {formData.cpu_cores} vCPU,{' '}
            {formData.memory_gb} GB RAM
          </Callout>
        </div>
      </Dialog>

      <CreateVolumeModal
        isOpen={showCreateVolumeModal}
        onClose={() => setShowCreateVolumeModal(false)}
        projectId={formData.project_id}
        onVolumeCreated={(volume) => {
          setSelectedVolume(volume);
          refetchVolumes();
        }}
      />

      <CreateSSHKeyModal
        isOpen={showCreateSSHKeyModal}
        onClose={() => setShowCreateSSHKeyModal(false)}
        onKeyCreated={(key) => {
          setFormData((prev) => ({ ...prev, ssh_key_id: key.id }));
          refetchSSHKeys();
        }}
      />

      <InsufficientBalanceModal
        isOpen={showInsufficientBalance}
        onClose={() => setShowInsufficientBalance(false)}
        message={balanceErrorMessage}
        requiredAmount={minimumRequired}
        currentBalance={availableBalance}
      />
    </Page>
  );
};

export default CreatePod;
